import { useEffect, useState } from "react";
import { Loader2, Search, Plus, Phone, MessageCircle, Trash2, Pencil, X, ClipboardList } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ActivityTimeline } from "@/components/admin/ActivityTimeline";
import { toast } from "sonner";

const REQ_STATUS_COLORS = {
  New: "bg-orange-100 text-orange-700", Active: "bg-blue-100 text-blue-700",
  Matched: "bg-indigo-100 text-indigo-700", Contacted: "bg-cyan-100 text-cyan-700",
  Negotiation: "bg-purple-100 text-purple-700", "Site Visit": "bg-amber-100 text-amber-700",
  "Deal In Process": "bg-violet-100 text-violet-700", Purchased: "bg-emerald-100 text-emerald-700",
  "Not Interested": "bg-slate-100 text-slate-500", Closed: "bg-slate-100 text-slate-500",
};

export const waLink = (num) => {
  if (!num) return null;
  const d = num.replace(/\D/g, "");
  return `https://wa.me/${d.length === 10 ? "91" + d : d}`;
};
const telLink = (num) => (num ? `tel:${num.replace(/[^\d+]/g, "")}` : null);
const fmtBudget = (a, b) => {
  if (!a && !b) return "—";
  const f = (n) => `₹${new Intl.NumberFormat("en-IN").format(n)}`;
  if (a && b) return `${f(a)} – ${f(b)}`;
  return f(a || b);
};

export default function AdminBuyers() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [profile, setProfile] = useState(null);
  const [showQuick, setShowQuick] = useState(false);
  const [editBuyer, setEditBuyer] = useState(null);
  const [reqBuyer, setReqBuyer] = useState(null);

  const load = () => {
    setLoading(true);
    const params = search ? { search } : {};
    api.get("/admin/buyers", { params }).then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [search]);
  useEffect(() => { api.get("/categories", { params: { all: true } }).then((r) => setCategories(r.data)).catch(() => {}); }, []);

  const openProfile = async (id) => {
    try { const { data } = await api.get(`/admin/buyers/${id}`); setProfile(data); }
    catch { toast.error("Failed to load buyer"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this buyer and all their requirements?")) return;
    try { await api.delete(`/admin/buyers/${id}`); toast.success("Deleted"); setProfile(null); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-extrabold text-slate-900">Buyers</h1>
        <button data-testid="add-buyer-requirement-btn" onClick={() => setShowQuick(true)}
          className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white">
          <Plus size={16} /> Add Buyer Requirement
        </button>
      </div>

      <div className="mt-4 relative max-w-md">
        <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
        <Input data-testid="buyer-search" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, mobile, company, city, buyer ID…" className="pl-9" />
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
        <div className="mt-5 grid gap-3 md:grid-cols-2">
          {items.length === 0 && <p className="py-12 text-center text-slate-500 md:col-span-2">No buyers yet.</p>}
          {items.map((b) => (
            <div key={b.id} data-testid={`buyer-row-${b.buyer_code}`} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                <button onClick={() => openProfile(b.id)} className="text-left">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[11px] text-slate-400">{b.buyer_code}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${b.status === "Active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{b.status}</span>
                  </div>
                  <p className="mt-0.5 font-semibold text-slate-900">{b.name}</p>
                  {b.company_name && <p className="text-sm text-slate-500">{b.company_name}</p>}
                  <p className="text-sm text-slate-500">📞 {b.mobile}{b.city ? ` • ${b.city}` : ""}</p>
                  <p className="mt-1 text-xs text-slate-400">{b.requirement_count} requirement(s) • {b.enquiry_count} enquiry(s)</p>
                </button>
                <div className="flex shrink-0 gap-1.5">
                  {telLink(b.mobile) && <a href={telLink(b.mobile)} className="rounded-lg border border-slate-200 p-2 text-slate-600" title="Call"><Phone size={14} /></a>}
                  {waLink(b.whatsapp) && <a href={waLink(b.whatsapp)} target="_blank" rel="noreferrer" className="rounded-lg bg-whatsapp p-2 text-white" title="WhatsApp"><MessageCircle size={14} /></a>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {profile && (
        <BuyerProfile buyer={profile} onClose={() => setProfile(null)} onEdit={() => setEditBuyer(profile)}
          onAddReq={() => setReqBuyer(profile)} onDelete={() => remove(profile.id)} onRefresh={() => openProfile(profile.id)} />
      )}
      {showQuick && <QuickAddModal categories={categories} onClose={() => setShowQuick(false)} onSaved={() => { setShowQuick(false); load(); }} />}
      {editBuyer && <BuyerFormModal buyer={editBuyer} onClose={() => setEditBuyer(null)}
        onSaved={() => { setEditBuyer(null); load(); if (profile) openProfile(profile.id); }} />}
      {reqBuyer && <RequirementFormModal buyer={reqBuyer} categories={categories} onClose={() => setReqBuyer(null)}
        onSaved={() => { setReqBuyer(null); if (profile) openProfile(profile.id); load(); }} />}
    </div>
  );
}

function BuyerProfile({ buyer, onClose, onEdit, onAddReq, onDelete, onRefresh }) {
  const setReqStatus = async (rid, status) => {
    try { await api.patch(`/admin/requirements/${rid}/status`, { status }); onRefresh(); }
    catch { toast.error("Failed"); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="buyer-profile" className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{buyer.name} <span className="font-mono text-xs text-slate-400">{buyer.buyer_code}</span></DialogTitle>
        </DialogHeader>
        <div className="rounded-lg bg-slate-50 p-3 text-sm">
          {buyer.company_name && <p className="font-semibold text-slate-800">{buyer.company_name}</p>}
          <p className="text-slate-600">📞 {buyer.mobile}{buyer.whatsapp ? ` • WA ${buyer.whatsapp}` : ""}</p>
          {buyer.email && <p className="text-slate-600">✉ {buyer.email}</p>}
          <p className="text-slate-600">{[buyer.city, buyer.state].filter(Boolean).join(", ")} • Source: {buyer.source}</p>
          {buyer.notes && <p className="mt-1 text-slate-500">{buyer.notes}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          {telLink(buyer.mobile) && <a href={telLink(buyer.mobile)} className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700"><Phone size={14} /> Call</a>}
          {waLink(buyer.whatsapp) && <a href={waLink(buyer.whatsapp)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-whatsapp px-3 py-1.5 text-sm font-semibold text-white"><MessageCircle size={14} /> WhatsApp</a>}
          <button data-testid="buyer-edit-btn" onClick={onEdit} className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700"><Pencil size={14} /> Edit</button>
          <button data-testid="buyer-delete-btn" onClick={onDelete} className="inline-flex items-center gap-1.5 rounded-full border border-red-200 px-3 py-1.5 text-sm font-semibold text-red-600"><Trash2 size={14} /> Delete</button>
        </div>

        <div className="mt-2 flex items-center justify-between">
          <h3 className="font-display font-bold text-slate-900">Requirements ({buyer.requirements?.length || 0})</h3>
          <button data-testid="add-requirement-btn" onClick={onAddReq} className="inline-flex items-center gap-1.5 rounded-full bg-brand px-3 py-1.5 text-xs font-bold text-white"><Plus size={13} /> Add Requirement</button>
        </div>
        <div className="space-y-2">
          {(buyer.requirements || []).length === 0 && <p className="text-sm text-slate-400">No requirements yet.</p>}
          {(buyer.requirements || []).map((r) => (
            <div key={r.id} className="rounded-lg border border-slate-200 p-3 text-sm">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-[11px] text-slate-400">{r.req_code}</span>
                <div className="w-40">
                  <Select value={r.status} onValueChange={(v) => setReqStatus(r.id, v)}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{["New","Active","Matched","Contacted","Negotiation","Site Visit","Deal In Process","Purchased","Not Interested","Closed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <p className="mt-1 font-semibold text-slate-800">{r.machine_name || r.category_name || "Requirement"}{r.brand ? ` • ${r.brand}` : ""}</p>
              <p className="text-slate-500">{r.category_name} • {r.machine_type} • Budget {fmtBudget(r.budget_min, r.budget_max)} • Qty {r.quantity}</p>
              {r.preferred_location && <p className="text-slate-500">📍 {r.preferred_location}</p>}
              {r.notes && <p className="mt-1 text-slate-500">"{r.notes}"</p>}
            </div>
          ))}
        </div>

        <h3 className="mt-3 font-display font-bold text-slate-900">Enquiries ({buyer.enquiries?.length || 0})</h3>
        <div className="space-y-2">
          {(buyer.enquiries || []).map((e) => (
            <div key={e.id} className="rounded-lg border border-slate-100 bg-slate-50 p-2 text-sm">
              <span className="font-mono text-[11px] text-slate-400">{e.enquiry_code}</span> — {e.machine_name || "General"}
              <span className="ml-2 rounded-full bg-white px-2 py-0.5 text-[10px] font-bold text-slate-500">{e.status}</span>
            </div>
          ))}
          {(buyer.enquiries || []).length === 0 && <p className="text-sm text-slate-400">No enquiries.</p>}
        </div>

        <h3 className="mt-3 font-display font-bold text-slate-900">Activity</h3>
        <ActivityTimeline entityType="buyer" entityId={buyer.id} refreshKey={buyer.requirements?.length || 0} />
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return <div><Label>{label}</Label>{children}</div>;
}

function BuyerFormModal({ buyer, onClose, onSaved }) {
  const [f, setF] = useState({
    name: buyer.name || "", company_name: buyer.company_name || "", mobile: buyer.mobile || "",
    whatsapp: buyer.whatsapp || "", email: buyer.email || "", city: buyer.city || "", state: buyer.state || "",
    source: buyer.source || "Admin Added", notes: buyer.notes || "", status: buyer.status || "Active",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.name.trim() || !f.mobile.trim()) { toast.error("Name and mobile required"); return; }
    setSaving(true);
    try { await api.put(`/admin/buyers/${buyer.id}`, f); toast.success("Saved"); onSaved(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="buyer-edit-modal" className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader><DialogTitle>Edit Buyer</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Name *"><Input data-testid="buyer-name" value={f.name} onChange={set("name")} /></Field>
          <Field label="Company"><Input value={f.company_name} onChange={set("company_name")} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Mobile *"><Input data-testid="buyer-mobile" value={f.mobile} onChange={set("mobile")} /></Field>
            <Field label="WhatsApp"><Input value={f.whatsapp} onChange={set("whatsapp")} /></Field>
          </div>
          <Field label="Email"><Input value={f.email} onChange={set("email")} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="City"><Input value={f.city} onChange={set("city")} /></Field>
            <Field label="State"><Input value={f.state} onChange={set("state")} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Status">
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["Active","Inactive","Closed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Source"><Input value={f.source} onChange={set("source")} /></Field>
          </div>
          <Field label="Notes"><Textarea rows={2} value={f.notes} onChange={set("notes")} /></Field>
          <button data-testid="buyer-save" onClick={save} disabled={saving} className="w-full rounded-full bg-brand py-2.5 font-bold text-white disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RequirementFormModal({ buyer, categories, onClose, onSaved }) {
  const [f, setF] = useState({ machine_name: "", category_id: "", machine_type: "any", brand: "", model: "",
    budget_min: "", budget_max: "", preferred_location: [buyer.city, buyer.state].filter(Boolean).join(", "),
    quantity: "1", urgency: "Normal", notes: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/requirements", { buyer_id: buyer.id, ...f,
        budget_min: Number(f.budget_min) || 0, budget_max: Number(f.budget_max) || 0, quantity: Number(f.quantity) || 1 });
      toast.success("Requirement added" + (data.match_count > 0 ? ` — ${data.match_count} match(es) found!` : "")); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="requirement-form-modal" className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader><DialogTitle>Add Requirement — {buyer.name}</DialogTitle></DialogHeader>
        <RequirementFields f={f} set={set} setF={setF} categories={categories} />
        <button data-testid="requirement-save" onClick={save} disabled={saving} className="mt-2 w-full rounded-full bg-brand py-2.5 font-bold text-white disabled:opacity-60">{saving ? "Saving…" : "Save Requirement"}</button>
      </DialogContent>
    </Dialog>
  );
}

export function RequirementFields({ f, set, setF, categories }) {
  return (
    <div className="space-y-3">
      <Field label="Machine / Part needed"><Input data-testid="req-machine-name" value={f.machine_name} onChange={set("machine_name")} placeholder="e.g. Used Excavator" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Category">
          <Select value={f.category_id || "none"} onValueChange={(v) => setF({ ...f, category_id: v === "none" ? "" : v })}>
            <SelectTrigger data-testid="req-category"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Any category</SelectItem>
              {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="New / Used">
          <Select value={f.machine_type} onValueChange={(v) => setF({ ...f, machine_type: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{["any","new","used"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Brand"><Input value={f.brand} onChange={set("brand")} /></Field>
        <Field label="Model"><Input value={f.model} onChange={set("model")} /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Budget from (₹)"><Input type="number" value={f.budget_min} onChange={set("budget_min")} /></Field>
        <Field label="Budget to (₹)"><Input type="number" value={f.budget_max} onChange={set("budget_max")} /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Preferred location"><Input value={f.preferred_location} onChange={set("preferred_location")} /></Field>
        <Field label="Quantity"><Input type="number" min="1" value={f.quantity} onChange={set("quantity")} /></Field>
      </div>
      <Field label="Urgency">
        <Select value={f.urgency} onValueChange={(v) => setF({ ...f, urgency: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{["Immediate","Within a month","Just exploring","Normal"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Notes"><Textarea rows={2} value={f.notes} onChange={set("notes")} /></Field>
    </div>
  );
}

function QuickAddModal({ categories, onClose, onSaved }) {
  const [f, setF] = useState({ name: "", mobile: "", whatsapp: "", email: "", company_name: "", city: "", state: "",
    machine_name: "", category_id: "", machine_type: "any", brand: "", model: "",
    budget_min: "", budget_max: "", preferred_location: "", quantity: "1", urgency: "Normal", message: "", source: "Phone" });
  const [saving, setSaving] = useState(false);
  const [dup, setDup] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const checkDup = async () => {
    if (!f.mobile.trim()) return;
    try { const { data } = await api.post("/admin/buyers/check-duplicate", { mobile: f.mobile });
      if (data.duplicate) setDup(data.buyer); else setDup(null);
    } catch { /* ignore */ }
  };

  const save = async () => {
    if (!f.name.trim() || !f.mobile.trim()) { toast.error("Name and mobile required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/quick-enquiry", { ...f,
        budget_min: Number(f.budget_min) || 0, budget_max: Number(f.budget_max) || 0, quantity: Number(f.quantity) || 1 });
      toast.success("Buyer requirement saved" + (data.match_count > 0 ? ` — ${data.match_count} matching machine(s) found!` : ""));
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="quick-add-modal" className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader><DialogTitle>Add Buyer Requirement</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Log a buyer who called, messaged or contacted Mashiniya. Creates buyer + requirement + enquiry.</p>
        <div className="mt-2 space-y-3">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Buyer</p>
          <Field label="Name *"><Input data-testid="quick-name" value={f.name} onChange={set("name")} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Mobile *"><Input data-testid="quick-mobile" value={f.mobile} onChange={set("mobile")} onBlur={checkDup} /></Field>
            <Field label="WhatsApp"><Input value={f.whatsapp} onChange={set("whatsapp")} /></Field>
          </div>
          {dup && <div className="rounded-lg bg-amber-50 p-2 text-xs text-amber-700" data-testid="dup-warning">Possible existing buyer: <strong>{dup.name}</strong> ({dup.buyer_code}). Saving will add a new requirement to this buyer.</div>}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Company"><Input value={f.company_name} onChange={set("company_name")} /></Field>
            <Field label="Email"><Input value={f.email} onChange={set("email")} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="City"><Input value={f.city} onChange={set("city")} /></Field>
            <Field label="State"><Input value={f.state} onChange={set("state")} /></Field>
          </div>
          <Field label="Source">
            <Select value={f.source} onValueChange={(v) => setF({ ...f, source: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{["Phone","WhatsApp","Referral","Facebook","Instagram","YouTube","Google","Admin Added","Other"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <p className="pt-1 text-xs font-bold uppercase tracking-wider text-slate-400">Requirement</p>
          <RequirementFields f={f} set={set} setF={setF} categories={categories} />
          <button data-testid="quick-save" onClick={save} disabled={saving} className="w-full rounded-full bg-brand py-2.5 font-bold text-white disabled:opacity-60">{saving ? "Saving…" : "Save Buyer Requirement"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
